import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.light.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import React from "react";
import { UserProvider, UserContext } from "./UserContext";
import LoginPage from "./components/LoginPage";
import ForgotPassword from "./views/auth/ForgotPassword";
import ForceChangePassword from "./views/auth/ForceChangePassword";

import FileViewerPage from "./components/FileViewerPage";
import AppRouter from "./components/AppRouter";
import { Route, Switch } from "react-router-dom";
import "./scss/style.scss";

const JointAppraisalReport = React.lazy(
  () => import("./views/projects/JointAppraisalReport"),
);

const LogFramePrint = React.lazy(() => import("./views/MEL/LogFramePrint"));

const LogFrameQuestionnaire = React.lazy(
  () => import("./views/MEL/LogFrameQuestionnaire"),
);

const showLoading = () => (
  <div className="animated fadeIn pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

// Containers
const TheLayout = React.lazy(() => import("./containers/TheLayout"));

const App = () => {
  return (
    <UserProvider>
      <UserContext.Consumer>
        {({ user, loading, login }) => {
          if (loading) return showLoading();

          return (
            <AppRouter user={user} login={login}>
              <React.Suspense fallback={showLoading()}>
                <Switch>
                  {/* Login page route */}
                  <Route
                    path="/login"
                    render={() =>
                      user ? (
                        <TheLayout />
                      ) : (
                        <LoginPage onLoginSuccess={login} />
                      )
                    }
                  />

                  {/* Forgot password route */}
                  <Route path="/forgot-password" component={ForgotPassword} />

                  {/* Force password change route — only accessible when forcePasswordChange is true */}
                  <Route
                    path="/force-change-password"
                    render={() => {
                      if (!user) {
                        window.location.hash = "#/login";
                        return null;
                      }
                      if (!user.forcePasswordChange) {
                        window.location.hash = "#/dashboard";
                        return null;
                      }
                      return <ForceChangePassword />;
                    }}
                  />

                  {/* File viewer route */}
                  <Route
                    path="/download/documents/:fileName"
                    component={FileViewerPage}
                  />

                  {/* Joint Appraisal Report - standalone page without layout */}
                  <Route
                    path="/jointAppraisalReport"
                    component={JointAppraisalReport}
                  />

                  {/* Log Frame print/preview - standalone page without layout */}
                  <Route
                    path="/log-frames-print/:id"
                    component={LogFramePrint}
                  />

                  {/* Anonymous LogFrame print/preview via token */}
                  <Route
                    path="/questionnaire/:token/print"
                    component={LogFramePrint}
                  />

                  {/* Anonymous LogFrame questionnaire - standalone page */}
                  <Route
                    path="/questionnaire/:token"
                    component={LogFrameQuestionnaire}
                  />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    render={(props) => {
                      if (!user) {
                        // Save current path so we can redirect back after login
                        const currentPath = window.location.hash.replace(
                          "#",
                          "",
                        );
                        if (currentPath && currentPath !== "/login") {
                          sessionStorage.setItem("returnUrl", currentPath);
                        }
                        // Redirect to login if not authenticated
                        window.location.hash = "#/login";
                        return null;
                      }
                      // Block access to the app until password is changed
                      if (user.forcePasswordChange) {
                        window.location.hash = "#/forgot-password";
                        return null;
                      }
                      return <TheLayout {...props} />;
                    }}
                  />
                </Switch>
              </React.Suspense>
              <ToastContainer />
            </AppRouter>
          );
        }}
      </UserContext.Consumer>
    </UserProvider>
  );
};
export default App;
